import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Banknote, RefreshCw, Building2, Wallet, Smartphone } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

function pickIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("banque")) return Building2;
  if (n.includes("mobile") || n.includes("momo")) return Smartphone;
  return Wallet;
}

export default function TresoreriePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/treasury");
      setData(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="tresorerie-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Banknote className="h-3 w-3" /><span>TRÉSORERIE — TEMPS RÉEL</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">État de trésorerie</h1>
          <p className="text-slate-500 mt-1">Soldes actuels de tous les comptes de banque et de caisse (Classe 5).</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="treso-refresh"><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
      </div>

      {data && (
        <>
          <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1e40af] rounded-3xl p-8 text-white shadow-xl" data-testid="treso-total">
            <div className="text-xs uppercase tracking-[0.25em] font-bold opacity-80 mb-2">Trésorerie totale disponible</div>
            <div className="font-heading font-extrabold text-5xl tabular-nums">{fmt(data.total_treasury)}</div>
            <div className="text-sm opacity-70 mt-3">Mis à jour : {new Date(data.as_of).toLocaleString("fr-FR")}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.accounts.map(a => {
              const Icon = pickIcon(a.name);
              const positive = a.solde >= 0;
              return (
                <div key={a.code} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`treso-${a.code}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-xs font-mono text-slate-400">{a.code}</div>
                      <div className="font-semibold text-[#1E3A8A] leading-tight">{a.name}</div>
                    </div>
                    <Icon className="h-6 w-6 text-[#1E3A8A] opacity-60" />
                  </div>
                  <div className={`text-2xl font-extrabold tabular-nums ${positive ? "text-[#166534]" : "text-red-600"}`}>{fmt(a.solde)}</div>
                </div>
              );
            })}
            {data.accounts.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <div className="text-slate-500">Aucun compte de trésorerie mouvementé pour l&apos;instant.</div>
                <div className="text-xs text-slate-400 mt-1">Initialisez le plan comptable et enregistrez vos premières écritures.</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
